// Leer el Excel/CSV de "Informacion de Companias".
//
// Victor Romero (agencia de Maricarmen Nieto), 2026-08-21, dos cosas:
//
//   1. "Al importar, une todo en un solo grupo en lugar de elegir el nombre de
//      las pestanas como te permite la pizarra de Boveda de Contrasenas."
//   2. "Solo me esta tomando la columna de correo Email. La columna de numeros
//      telefonicos la deja en blanco, sobre todo si en la celda hay mas de un
//      numero o texto adicional."
//
// LAS DOS SON EL MISMO FALLO. El lector reconocia exactamente cuatro nombres de
// columna —"Name", "TELEFONOS", "LINKS", "EMAIL"— porque se escribio mirando UN
// archivo concreto. Las columnas de Victor son "Elemento, Numeros, Link de
// Pago, Correo electronico":
//
//   · "Elemento" no es "name", asi que la deteccion de cabecera DEVOLVIA NULL
//     y la fila de titulos se trataba como un dato mas.
//   · Sin cabecera, el lector caia a posiciones fijas: nombre=col 0,
//     telefonos=col 2, links=col 3. En su archivo la col 2 es "Link de Pago",
//     asi que el telefono salia vacio y el enlace traia el correo.
//   · El correo si aparecia porque hay un rescate aparte que busca una "@" en
//     cualquier celda. De ahi su "solo me toma la columna de correo".
//   · Y sin cabecera tampoco se reconocian sus banderas de seccion, asi que
//     todo caia en la categoria por defecto: "une todo en un solo grupo".
//
// La correccion tiene dos capas. Primera: reconocer MUCHOS mas nombres, sin
// acentos y sin distinguir mayusculas. Segunda, la que de verdad cierra el
// problema: que el agente pueda MAPEAR las columnas a mano y escoger la
// categoria, como ya hace el Baul de Contrasenas. Ninguna lista de nombres de
// columna va a cubrir todos los Excel del mundo; poder decirlo a mano, si.

const trocear = (v) => String(v ?? '').trim();

export const CAMPOS_IMPORT = ['name', 'phones', 'links', 'email', 'trainingUrl'];

// Sin acentos, sin mayusculas, sin espacios de sobra. "Números" y "numeros"
// tienen que ser la misma palabra.
export function normalizar(v) {
  return String(v ?? '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .trim().toLowerCase();
}

// El vocabulario de cada campo. El orden importa: se comprueba de arriba abajo
// y gana el primero, asi que "correo" (email) va antes que cualquier cosa mas
// general.
const VOCABULARIO = [
  ['email', ['email', 'e-mail', 'correo', 'mail']],
  ['phones', ['telefono', 'telefonos', 'numero', 'numeros', 'number', 'phone', 'tel.', 'tel ', 'celular', 'movil', 'whatsapp', 'fax', 'extension']],
  ['links', ['link', 'links', 'url', 'enlace', 'pago', 'payment', 'portal', 'sitio', 'website', 'pagina']],
  ['trainingUrl', ['video', 'entrenamiento', 'training', 'capacitacion']],
  ['name', ['name', 'nombre', 'elemento', 'item', 'compania', 'company', 'proveedor', 'empresa', 'titulo', 'concepto', 'descripcion', 'aseguradora', 'carrier']],
];

// A que campo suena esta cabecera. Devuelve null si no suena a nada.
export function campoDeCabecera(texto) {
  const n = normalizar(texto);
  if (!n) return null;
  for (const [campo, palabras] of VOCABULARIO) {
    // Coincidencia exacta primero: una columna llamada justo "name" o "correo"
    // no debe perder contra una palabra suelta de otro campo.
    if (palabras.includes(n)) return campo;
  }
  // Segunda pasada: la palabra tiene que EMPEZAR la cabecera o ir suelta.
  // Buscarla en cualquier posicion hacia que "Subelementos" contuviera
  // "elemento" y se tomara por la columna del Nombre.
  for (const [campo, palabras] of VOCABULARIO) {
    for (const bruta of palabras) {
      const pal = bruta.trim();
      if (pal.length <= 2) continue;
      if (n.startsWith(pal) || n.includes(' ' + pal) || n.includes(pal + ' ')) return campo;
    }
  }
  return null;
}

/**
 * Los indices de columna de una fila de titulos, o null si no lo parece.
 *
 * Antes exigia encontrar una columna llamada "name" y devolvia null sin ella —
 * que es lo que rompio el archivo de Victor. Ahora basta con reconocer DOS
 * campos: si hay telefonos y correo, es una fila de titulos aunque la primera
 * columna se llame "Elemento", "Concepto" o cualquier cosa. Y si no se
 * reconocio ninguna columna de nombre, se toma la primera que sobre.
 */
export function detectarCabecera(fila) {
  const r = Array.isArray(fila) ? fila : [];
  const idx = { name: -1, phones: -1, links: -1, email: -1, trainingUrl: -1 };
  const usadas = new Set();
  r.forEach((celda, i) => {
    const campo = campoDeCabecera(celda);
    if (!campo) return;
    // La ULTIMA columna de correo gana: en estos archivos la de la derecha es
    // la que trae la direccion de verdad.
    if (campo === 'email') { idx.email = i; usadas.add(i); return; }
    if (idx[campo] < 0) { idx[campo] = i; usadas.add(i); }
  });
  // Basta con reconocer DOS columnas. Antes se exigia una llamada "name" y sin
  // ella se devolvia null — que es lo que rompio el archivo de Victor.
  const reconocidas = Object.values(idx).filter((v) => v >= 0).length;
  if (reconocidas < 2) return null;
  if (idx.name < 0) {
    // Se reconocieron telefonos/correo pero ninguna columna suena a nombre
    // ("Cosa", "Item 1"...). Se toma la primera que no se haya usado ya.

    const libre = r.findIndex((c, i) => !usadas.has(i) && normalizar(c));
    idx.name = libre >= 0 ? libre : 0;
  }
  return idx;
}

// Las etiquetas de cabecera del archivo, para pintarlas en el mapeo manual.
// Devuelve { fila, cabeceras } de la PRIMERA fila que parezca de titulos.
export function cabecerasDe(grid) {
  const filas = Array.isArray(grid) ? grid : [];
  for (let i = 0; i < filas.length; i++) {
    const idx = detectarCabecera(filas[i]);
    if (idx) return { fila: i, cabeceras: (filas[i] || []).map((c) => String(c ?? '').trim()), detectado: idx };
  }
  // Sin fila de titulos RECONOCIBLE, se toma la primera fila util con mas de
  // una celda: casi siempre es la de titulos, solo que con nombres que el
  // vocabulario no conoce — que es exactamente el caso que el mapeo manual
  // viene a resolver. Se devuelve su numero para poder saltarla.
  const util = filas.findIndex((f) => (Array.isArray(f) ? f : []).map(trocear).filter(Boolean).length > 1);
  if (util >= 0) {
    return { fila: util, cabeceras: (filas[util] || []).map((c, i) => trocear(c) || `Columna ${i + 1}`), detectado: null };
  }
  return { fila: -1, cabeceras: [], detectado: null };
}

/**
 * Recorre la hoja y devuelve una fila por compania.
 *
 * `opciones.mapeo`   { [indiceColumna]: campo } puesto a mano por el agente.
 *                    Manda sobre cualquier deteccion automatica.
 * `opciones.categoria` 'auto' (detectar por las banderas de seccion) o una de
 *                    las categorias fijas, que se aplica a TODAS las filas.
 */
export function parseCompanyRows(grid, opciones = {}) {
  const mapeo = opciones.mapeo && Object.keys(opciones.mapeo).length ? opciones.mapeo : null;
  const forzada = opciones.categoria && opciones.categoria !== 'auto' ? opciones.categoria : null;
  // Con mapeo manual no se puede confiar en reconocer la fila de titulos —
  // justo se mapea a mano porque sus nombres no se reconocen. Se salta por
  // numero de fila, que es el que dio `cabecerasDe`.
  const filaCabecera = Number.isInteger(opciones.filaCabecera) ? opciones.filaCabecera : -1;

  const out = [];
  let category = 'contacts';
  let cols = null;

  // Columnas fijadas a mano: valen para toda la hoja desde el principio.
  if (mapeo) {
    cols = { name: -1, phones: -1, links: -1, email: -1, trainingUrl: -1 };
    Object.entries(mapeo).forEach(([i, campo]) => {
      if (campo && cols[campo] !== undefined && cols[campo] < 0) cols[campo] = Number(i);
    });
  }

  const filas = Array.isArray(grid) ? grid : [];
  for (let fi = 0; fi < filas.length; fi++) {
    if (mapeo && filaCabecera >= 0 && fi <= filaCabecera) continue;
    const raw = filas[fi];
    const r = Array.isArray(raw) ? raw : [];
    const joined = r.map(trocear).filter(Boolean);
    if (!joined.length) continue;
    const first = normalizar(r[0]);

    // Banderas de seccion. Solo mandan cuando la categoria es automatica.
    if (!forzada) {
      if (first.includes('telefonos') && first.includes('correo')) { category = 'contacts'; continue; }
      if (joined.length === 1 && first === 'links') { category = 'links'; continue; }
      if (first.includes('video') && first.includes('entrenamiento')) { category = 'training'; continue; }
    } else {
      // Con categoria forzada las banderas siguen siendo filas de adorno: se
      // saltan igual, pero sin cambiar nada.
      if ((first.includes('telefonos') && first.includes('correo'))
        || (joined.length === 1 && first === 'links')
        || (first.includes('video') && first.includes('entrenamiento'))) continue;
    }
    // Filas de adorno del propio Monday.
    if (first.includes('monday.com') || first.includes('try it free') || (first.includes('numeros') && first.includes('compa'))) continue;

    // Fila de titulos: se recuerda el reparto y no se emite... salvo que el
    // agente haya mapeado a mano, en cuyo caso solo hay que saltarla.
    const hdr = detectarCabecera(r);
    if (hdr) { if (!mapeo) cols = hdr; continue; }

    const at = (i) => (i >= 0 && i < r.length ? trocear(r[i]) : '');
    const name = cols && cols.name >= 0 ? at(cols.name) : trocear(r[0]);
    if (!name) continue;
    // Sin cabecera NI mapeo se cae a posiciones fijas, que es lo que habia
    // antes. Se conserva para los archivos que ya funcionaban.
    const phones = cols ? at(cols.phones) : trocear(r[2]);
    const rawLink = cols ? (cols.links >= 0 ? at(cols.links) : at(cols.trainingUrl)) : trocear(r[3]);
    const training = cols && cols.trainingUrl >= 0 ? at(cols.trainingUrl) : '';

    let emailCell = cols && cols.email >= 0 ? at(cols.email) : '';
    if (!emailCell && !mapeo) {
      // Rescate: buscar una "@" en cualquier celda. Solo cuando el agente NO
      // mapeo a mano — si dijo donde esta el correo, se respeta.
      emailCell = r.map(trocear).find((c) => c.includes('@')) || '';
    }
    const email = emailCell.split(/[\n,;/]+/).map((p) => p.trim()).filter((p) => p.includes('@')).join(' / ');

    // La categoria forzada MANDA. Antes se guardaba `category`, que con una
    // categoria escogida a mano se quedaba en el valor por defecto: el
    // selector no hacia nada.
    const cat = forzada || category;
    const esVideo = cat === 'training';
    const rowObj = {
      name: name.slice(0, 300),
      phones: phones.slice(0, 4000),
      links: esVideo ? '' : rawLink.slice(0, 8000),
      email: email.slice(0, 4000),
      trainingUrl: (esVideo ? (training || rawLink) : training).slice(0, 4000),
      category: cat,
    };
    if (!rowObj.phones && !rowObj.links && !rowObj.email && !rowObj.trainingUrl && rowObj.name.length < 2) continue;
    out.push(rowObj);
  }
  return out;
}
